/**
 * RescheduleModal — quick reschedule for a Job's install date.
 *
 * Push-Mode aware: on confirm the modal stays open and renders an inline
 * `PushNextStepPanel` so the operator can chain into the next deterministic
 * action (Assign Crew, Resolve Issue, etc.) without bouncing pages.
 */
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar, Clock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import type { Job, Issue } from "@/data/types";
import { JobService } from "@/services/job.service";
import { clampInstallShifts, getInstallCalendarShiftCountFromRange } from "@/data/scheduling/installSpan";
import { PushProgressTracker } from "@/components/PushProgressTracker";
import { stageKeyToPushStep, type TrackerStageKey } from "@/lib/pushStageNavigation";
import { PushNextStepPanel, type PushNextStep } from "@/components/quick-actions/PushNextStepPanel";
import { PushContextHeader } from "@/components/quick-actions/PushContextHeader";
import { buildPushHandoff } from "@/data/pushHandoffEngine";
import { displayIdentity } from "@/lib/uuid";

export interface RescheduleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job | null;
  /** Open issues for this job — feeds the in-context next-step engine. */
  openIssues?: Issue[];
  /** Fired after a successful reschedule with the projected updated job. */
  onSuccess?: (updatedJob: Job) => void;
  /** Push-Mode chain: render a "Back" button. Hidden once edits start. */
  onBack?: () => void;
  /** Push-Mode chain: operator picked a next step from the success panel. */
  onChooseNextStep?: (step: PushNextStep, updatedJob: Job) => void;
}

export function RescheduleModal({ open, onOpenChange, job, openIssues = [], onSuccess, onBack, onChooseNextStep }: RescheduleModalProps) {
  const { setJobs, setFollowUpTasks } = useAppData();
  const { roleInfo } = useRole();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [reason, setReason] = useState("");
  const [dirty, setDirty] = useState(false);
  const [sent, setSent] = useState(false);
  const [updatedJob, setUpdatedJob] = useState<Job | null>(null);

  const toDateInputValue = (value?: string | null) => {
    if (!value) return "";
    const raw = String(value);
    const dateOnly = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateOnly) return dateOnly[1];
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
  };

  const toTimeInputValue = (value?: string | null) => {
    if (!value) return "";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "" : d.toTimeString().slice(0, 5);
  };

  const formatScheduleRange = (rangeStart: string, rangeEnd: string, rangeTime: string) => {
    const startLabel = rangeStart;
    const endLabel = rangeEnd && rangeEnd !== rangeStart ? ` → ${rangeEnd}` : "";
    return `${startLabel}${endLabel} at ${rangeTime}`;
  };

  useEffect(() => {
    if (!open) return;
    const existingStartDate = toDateInputValue(job?.scheduledStartTime ?? job?.scheduledInstallDate);
    const existingEndDate = toDateInputValue(job?.scheduledEndTime);
    const existingStartTime = toTimeInputValue(job?.scheduledStartTime);
    if (existingStartDate) {
      setStartDate(existingStartDate);
      setEndDate(existingEndDate || existingStartDate);
      setTime(existingStartTime || "09:00");
    } else {
      // Smart default: tomorrow at 9:00 AM (or Monday if tomorrow is weekend)
      const d = new Date();
      d.setDate(d.getDate() + 1);
      while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
      const defaultDate = d.toISOString().slice(0, 10);
      setStartDate(defaultDate);
      setEndDate(defaultDate);
      setTime("09:00");
    }
    setReason("");
    setDirty(false);
    setSent(false);
    setUpdatedJob(null);
  }, [job?.id, job?.scheduledInstallDate, job?.scheduledStartTime, job?.scheduledEndTime, open]);

  if (!job) return null;

  const handleConfirm = async () => {
    if (!startDate) {
      toast.error("Pick a start date first");
      return;
    }
    if (!endDate) {
      toast.error("Pick an end date first");
      return;
    }
    const start = new Date(`${startDate}T${time}:00`);
    const end = endDate === startDate
      ? new Date(start.getTime() + 4 * 60 * 60 * 1000)
      : new Date(`${endDate}T17:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      toast.error("Schedule range is invalid");
      return;
    }
    if (end < start) {
      toast.error("End date must be on or after the start date");
      return;
    }
    const startIso = start.toISOString();
    const endIso = end.toISOString();
    const scheduledShifts = clampInstallShifts(getInstallCalendarShiftCountFromRange(startIso, endIso));
    const rangeLabel = formatScheduleRange(startDate, endDate, time);
    const previousJob = job;
    let next: Job | null = null;
    setJobs((prev) =>
      prev.map((j) => {
        if (j.id !== job.id) return j;
        const updated: Job = {
          ...j,
          scheduledInstallDate: startDate,
          scheduledStartTime: startIso,
          scheduledEndTime: endIso,
          scheduledShifts,
          activityHistory: [
            ...(j.activityHistory ?? []),
            {
              id: `act-${Date.now()}`,
              date: new Date().toISOString(),
              user: "User",
              action: `Rescheduled install to ${rangeLabel}${reason ? ` — ${reason}` : ""}`,
              noteType: "system" as const,
            },
          ],
        };
        next = updated;
        return updated;
      }),
    );
    if (!next) return;

    const { data, error } = await JobService.update(job.id, {
      scheduledInstallDate: startDate,
      scheduledStartTime: startIso,
      scheduledEndTime: endIso,
      scheduledShifts,
      activityHistory: next.activityHistory,
    });
    if (error || !data) {
      setJobs((prev) => prev.map((j) => (j.id === previousJob.id ? previousJob : j)));
      toast.error("Reschedule failed", { description: "Could not persist the new schedule date/time." });
      return;
    }

    setJobs((prev) => prev.map((j) => (j.id === data.id ? data : j)));
    toast.success(`Rescheduled to ${rangeLabel}`);
    setUpdatedJob(data);
    setSent(true);
    if (onSuccess) onSuccess(data);
  };

  /** Hand-off: create a tracked FollowUpTask + audit entry. Owner + due date confirmed by operator. */
  const handleHandOffConfirmed = (
    assignee: string,
    dueIso: string,
    selection?: import("./PushHandoffPrompt").PushHandoffSelection,
  ) => {
    if (!updatedJob) { onOpenChange(false); return; }
    const { task, activityEntry } = buildPushHandoff({
      job: updatedJob,
      context: { reasonFragment: "after install reschedule", sourceLabel: "Reschedule" },
      assignee,
      assigneeRoleKey: selection?.roleKey ?? null,
      assigneeUserId: selection?.userId ?? null,
      dueDate: dueIso,
      initiatedBy: roleInfo.label,
    });
    setFollowUpTasks((prev) => [...prev, task]);
    setJobs((prev) =>
      prev.map((j) =>
        j.id !== updatedJob.id ? j : { ...j, activityHistory: [...(j.activityHistory ?? []), activityEntry] },
      ),
    );
    toast.success(`Handed off to ${assignee} — due ${dueIso.slice(0, 10)}`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" /> Reschedule Install
          </DialogTitle>
          <DialogDescription>
            <span className="break-all">{job.customerName} — {displayIdentity(job.jobNumber || job.id, "JOB")}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <PushContextHeader job={updatedJob ?? job} openIssues={openIssues} />
          <PushProgressTracker
            job={updatedJob ?? job}
            activeKey={sent ? undefined : "schedule"}
            onStageClick={(key) => {
              if (key === "schedule") return;
              const target = updatedJob ?? job;
              onChooseNextStep?.(stageKeyToPushStep(key as TrackerStageKey, target.id), target);
            }}
          />

          {sent && updatedJob ? (
            <PushNextStepPanel
              job={updatedJob}
              openIssues={openIssues}
              headline="Rescheduled — what's next?"
              summary={`Install set to ${formatScheduleRange(startDate, endDate, time)}`}
              autoArm
              onContinue={(step) => {
                if (onChooseNextStep) onChooseNextStep(step, updatedJob);
                else onOpenChange(false);
              }}
              onPause={() => onOpenChange(false)}
              onHandOffConfirmed={handleHandOffConfirmed}
              handoffReasonFragment="after install reschedule"
            />
          ) : (
            <>
              {/* Password-manager trap fields: keep browser/extension overlays
                  out of scheduling controls (date/time are not credentials). */}
              <div className="sr-only" aria-hidden="true">
                <input type="text" name="username" autoComplete="username" tabIndex={-1} />
                <input type="password" name="password" autoComplete="current-password" tabIndex={-1} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 items-start" autoComplete="off">
                <div className="min-w-0 space-y-1.5">
                  <Label htmlFor="r-start-date" className="text-xs leading-none">Start Date</Label>
                  <Input
                    id="r-start-date"
                    name="reschedule-start-date"
                    type="date"
                    autoComplete="new-password"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    data-form-type="other"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    value={startDate}
                    onChange={(e) => {
                      const nextStart = e.target.value;
                      setStartDate(nextStart);
                      if (!endDate || (nextStart && endDate < nextStart)) setEndDate(nextStart);
                      setDirty(true);
                    }}
                    className="h-12 pr-12"
                  />
                </div>
                <div className="min-w-0 space-y-1.5">
                  <Label htmlFor="r-end-date" className="text-xs leading-none">End Date</Label>
                  <Input
                    id="r-end-date"
                    name="reschedule-end-date"
                    type="date"
                    min={startDate || undefined}
                    autoComplete="new-password"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    data-form-type="other"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setDirty(true); }}
                    className="h-12 pr-12"
                  />
                </div>
                <div className="min-w-0 space-y-1.5 md:col-span-2">
                  <Label htmlFor="r-time" className="text-xs leading-none">Start Time</Label>
                  <Input
                    id="r-time"
                    name="reschedule-time"
                    type="time"
                    autoComplete="new-password"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    data-form-type="other"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    value={time}
                    onChange={(e) => { setTime(e.target.value); setDirty(true); }}
                    className="h-12 pr-12"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="r-reason" className="text-xs">Reason (optional)</Label>
                <Textarea
                  id="r-reason"
                  name="reschedule-reason"
                  autoComplete="off"
                  data-1p-ignore="true"
                  data-lpignore="true"
                  placeholder="Customer requested earlier date, weather delay, etc."
                  value={reason}
                  onChange={(e) => { setReason(e.target.value); setDirty(true); }}
                  rows={2}
                  className="mt-1 resize-none"
                />
              </div>
            </>
          )}
        </div>

        {!sent && (
          <DialogFooter className="gap-2 sm:gap-2">
            {onBack && !dirty && (
              <Button variant="ghost" size="sm" onClick={onBack} className="mr-auto gap-1.5">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleConfirm} disabled={!startDate || !endDate}>
              {startDate && endDate ? "Confirm Reschedule (ready)" : "Confirm Reschedule"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
